const GROUP_PATTERNS = [
  ['pins', /pin|starter|booster/i],
  ['plush', /plush/i],
  ['patches', /patch/i],
  ['mystery', /mystery/i],
];

class MixedNewArrivals extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.load();
  }

  async load() {
    const status = this.querySelector('[data-status]');
    const grid = this.querySelector('[data-grid]');
    const limit = Number.parseInt(this.dataset.limit || '4', 10);

    try {
      const response = await fetch(this.dataset.endpoint, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`New arrivals request failed: ${response.status}`);

      const payload = await response.json();
      const products = this.selectProducts(payload.products || [], limit);

      if (products.length === 0) throw new Error('No eligible new arrivals found');

      grid.replaceChildren(...products.map((product) => this.createCard(product)));
      grid.classList.remove('pc-new-arrivals__grid--loading');
      grid.removeAttribute('aria-hidden');
      status.hidden = true;
      this.setAttribute('aria-busy', 'false');
    } catch (error) {
      console.error(error);
      grid.replaceChildren();
      grid.classList.remove('pc-new-arrivals__grid--loading');
      grid.removeAttribute('aria-hidden');
      status.hidden = false;
      status.classList.remove('visually-hidden');
      status.textContent = 'New arrivals are temporarily unavailable.';
      this.setAttribute('aria-busy', 'false');
    }
  }

  selectProducts(products, limit) {
    const eligible = products
      .filter((product) => {
        const vendorAndTitle = `${product.vendor || ''} ${product.title || ''}`;
        const price = Number.parseFloat(product.variants?.[0]?.price || '0');
        const hasAvailableVariant = product.variants?.some((variant) => variant.available) || false;
        return (
          product.images?.[0]?.src &&
          hasAvailableVariant &&
          price > 0 &&
          !/universal/i.test(vendorAndTitle)
        );
      })
      .sort((a, b) => new Date(b.published_at) - new Date(a.published_at));

    const selected = [];
    const selectedIds = new Set();
    const selectedGroups = new Set();

    for (const product of eligible) {
      const group = this.productGroup(product.product_type);
      if (selectedGroups.has(group)) continue;
      selected.push(product);
      selectedIds.add(product.id);
      selectedGroups.add(group);
      if (selected.length === limit) return selected;
    }

    for (const product of eligible) {
      if (selectedIds.has(product.id)) continue;
      selected.push(product);
      if (selected.length === limit) break;
    }

    return selected;
  }

  productGroup(productType = '') {
    for (const [group, pattern] of GROUP_PATTERNS) {
      if (pattern.test(productType)) return group;
    }
    return productType.trim().toLowerCase() || 'other';
  }

  createCard(product) {
    const article = document.createElement('article');
    article.className = 'pc-new-arrival-card';

    const link = document.createElement('a');
    link.className = 'pc-new-arrival-card__link';
    link.href = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;

    const media = document.createElement('div');
    media.className = 'pc-new-arrival-card__media';

    const image = document.createElement('img');
    image.className = 'pc-new-arrival-card__image';
    image.src = this.imageUrl(product.images[0].src, 720);
    image.srcset = [360, 540, 720]
      .map((width) => `${this.imageUrl(product.images[0].src, width)} ${width}w`)
      .join(', ');
    image.sizes = '(max-width: 749px) 50vw, 25vw';
    image.alt = product.title;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.width = 720;
    image.height = 900;

    const title = document.createElement('h3');
    title.className = 'pc-new-arrival-card__title';
    title.textContent = product.title;

    const price = document.createElement('p');
    price.className = 'pc-new-arrival-card__price';
    price.textContent = this.formatPrice(product.variants[0].price);

    media.append(image);
    link.append(media, title, price);
    article.append(link);
    return article;
  }

  imageUrl(source, width) {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('width', String(width));
    return url.toString();
  }

  formatPrice(value) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    }).format(Number.parseFloat(value));
  }
}

if (!customElements.get('mixed-new-arrivals')) {
  customElements.define('mixed-new-arrivals', MixedNewArrivals);
}
